/**
 * Runtime configuration fetched from the backend.
 * Provides snap config with self-healing fallback.
 *
 * Fallback chain:
 *   1. GET /api/config (runtime, from backend)
 *   2. import.meta.env.VITE_SNAP_ID (build-time)
 *   3. Error state (snapConfigured = false)
 */

interface RuntimeConfig {
  snapId: string | null;
  snapVersion: string | null;
  latestVersion: string | null;
  environment: string;
  snapConfigured: boolean;
}

let cachedConfig: RuntimeConfig | null = null;
let fetchPromise: Promise<RuntimeConfig> | null = null;

/**
 * Fetch runtime config from backend.
 * Deduplicates concurrent calls via a shared promise.
 * Falls back to build-time env vars if backend unavailable.
 */
export async function getRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) return cachedConfig;

  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const res = await fetch('/api/config', {
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) {
        const data = await res.json();
        cachedConfig = {
          snapId: data.snapId ?? null,
          snapVersion: data.snapVersion ?? null,
          latestVersion: data.latestVersion ?? null,
          environment: data.environment ?? 'unknown',
          snapConfigured: !!data.snapConfigured,
        };
        return cachedConfig;
      }
    } catch {
      // Backend unavailable — fall through to build-time values
    }

    // Fallback to build-time env vars (existing behavior)
    const buildTimeId: string = import.meta.env.VITE_SNAP_ID || '';
    const parsed = parseSnapIdLocal(buildTimeId);
    cachedConfig = {
      snapId: parsed.origin || null,
      snapVersion: parsed.version ?? null,
      latestVersion: null,
      environment: import.meta.env.MODE || 'unknown',
      snapConfigured: !!parsed.origin,
    };
    return cachedConfig;
  })();

  const result = await fetchPromise;
  fetchPromise = null;
  return result;
}

function parseSnapIdLocal(id: string): { origin: string; version?: string } {
  const match = id.match(/^(npm:(?:@[^/]+\/)?[^@]+)(?:@(.+))?$/);
  if (match) return { origin: match[1], version: match[2] };
  return { origin: id };
}

/** For testing — resets the cached config so getRuntimeConfig fetches again. */
export function _resetRuntimeConfig(): void {
  cachedConfig = null;
  fetchPromise = null;
}
